import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../components/SparkleEffect';
import { CheckIcon, SparklesIcon, LockIcon, ClipboardListIcon, ArrowRightIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
function CourseSignupForm({
  formId,
  buttonText = '💖 Notify Me'
}: {
  formId: string;
  buttonText?: string;
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const formData = new FormData();
      formData.append('fields[email]', email);
      formData.append('ml-submit', '1');
      formData.append('anticsrf', 'true');
      await fetch(`https://assets.mailerlite.com/jsonp/2211088/forms/${formId}/subscribe`, {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-6 text-center border border-lavender-200/50" data-id="element-1470">
        <p className="font-heading text-xl font-bold text-plum-700 mb-1" data-id="element-1471">
          💖 Thank you!
        </p>
        <p className="text-plum-500 text-sm font-medium" data-id="element-1472">
          You'll be the first to know when it drops.
        </p>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-3" data-id="element-1473">
      <label className="block text-sm font-bold text-plum-600 mb-1" data-id="element-1474">
        Get notified when it's available
      </label>
      <div className="flex gap-2" data-id="element-1475">
        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" className="flex-1 px-4 py-3 rounded-full border border-plum-200/50 bg-white/40 text-plum-700 text-sm font-medium placeholder:text-plum-300 focus:outline-none focus:ring-2 focus:ring-cta-400 focus:border-transparent" data-id="element-1476" />
        <button type="submit" disabled={status === 'loading'} className="px-6 py-3 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-colors shadow-md shadow-cta-500/20 whitespace-nowrap disabled:opacity-60" data-id="element-1477">
          {status === 'loading' ? '...' : buttonText}
        </button>
      </div>
      {status === 'error' && <p className="text-red-500 text-xs font-medium" data-id="element-1478">
          Something went wrong. Please try again.
        </p>}
    </form>;
}
export function CoursesPage() {
  return <div className="min-h-screen pt-24 pb-24 relative" data-id="element-1479">
      {/* Full background image */}
      <img src="https://cdn.magicpatterns.com/uploads/oG4RFfihdYAxazP48DHh8y/ChatGPT_Image_Mar_21,_2026,_09_37_08_PM.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain object-top pointer-events-none z-0" data-id="element-1480" />
      {/* Soft overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/70 z-0" data-id="element-1481" />

      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden" data-id="element-1482">
        <SparkleEffect count={90} data-id="element-1483" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-1484">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-1485">
            Trippin' Through{' '}
            <span className="text-cta-500" data-id="element-1486">Transformation</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-1487">
            Real tools for real transformation. No fluff, no gatekeeping.
          </motion.p>
        </div>
      </section>

      {/* Featured Course */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1488">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative bg-gradient-to-br from-blush-100/40 to-white/40 backdrop-blur-[2px] border border-blush-200/50 rounded-[3rem] p-8 md:p-12 shadow-xl overflow-hidden" data-id="element-1489">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-1490" />

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cta-500 text-white text-sm font-bold mb-8 shadow-md uppercase tracking-wider" data-id="element-1491">
            <GlitterHeart size={16} variant="light" data-id="element-1492" />
            Flagship Course
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-1493">
            <div data-id="element-1494">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-700 mb-4" data-id="element-1495">
                The Broken Beginning
              </h2>
              <p className="text-xl text-cta-500 font-bold mb-6" data-id="element-1496">
                Your first step from surviving to thriving.
              </p>
              <p className="text-plum-700 leading-relaxed mb-8 font-medium" data-id="element-1497">
                A course for anyone who's been knocked down by life and is ready
                to get back up — stronger, sassier, and unapologetically
                themselves. This isn't about toxic positivity. It's about doing
                the real work to rebuild your confidence, rewrite your story,
                and step into the badass you were always meant to be.
              </p>

              <a href="https://trippinwithmandy.gumroad.com/l/bgkefq" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full md:w-auto px-8 py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-900 transition-all shadow-lg hover:-translate-y-1" data-id="element-1498">
                <GlitterHeart size={20} variant="light" data-id="element-1499" />
                Enroll on Gumroad
              </a>
            </div>

            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 border border-white/50" data-id="element-1500">
              <h3 className="font-bold text-plum-700 text-xl mb-6" data-id="element-1501">
                What you'll learn:
              </h3>
              <ul className="space-y-4" data-id="element-1502">
                {['How to stop letting your past define your future', 'Building unshakeable confidence from the inside out', 'Setting boundaries without guilt', 'Turning your pain into your superpower', 'Daily mindset practices that actually stick'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1503">
                    <div className="mt-1 bg-blush-200 rounded-full p-1 shrink-0" data-id="element-1504">
                      <CheckIcon className="w-4 h-4 text-plum-700" data-id="element-1505" />
                    </div>
                    <span className="text-plum-700 font-medium" data-id="element-1506">{feature}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Checklists CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-1507">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative bg-gradient-to-br from-white/50 to-lavender-50/50 backdrop-blur-sm border border-lavender-200/50 rounded-2xl p-6 md:p-8 shadow-sm overflow-hidden" data-id="element-1508">
          <div className="absolute top-4 right-4 px-3 py-1 bg-cta-500/90 text-white text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1" data-id="element-1509">
            <SparklesIcon className="w-3 h-3" data-id="element-1510" /> More Added Daily
          </div>

          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6" data-id="element-1511">
            <div className="w-12 h-12 bg-white/80 rounded-xl flex items-center justify-center shadow-sm shrink-0" data-id="element-1512">
              <ClipboardListIcon className="w-6 h-6 text-cta-500" data-id="element-1513" />
            </div>

            <div className="flex-grow text-center sm:text-left" data-id="element-1514">
              <h3 className="font-heading text-xl font-bold text-plum-700 mb-1" data-id="element-1515">
                Individual Checklists
              </h3>
              <p className="text-plum-500 font-medium text-sm" data-id="element-1516">
                Grab exactly what you need — interactive checklists & printable
                PDFs starting at $1.99.
              </p>
            </div>

            <Link to="/checklists" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-all shadow-md hover:-translate-y-0.5 whitespace-nowrap shrink-0" data-id="element-1517">
              Browse
              <ArrowRightIcon className="w-4 h-4" data-id="element-1518" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Coming Soon Courses */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1519">
        <div className="text-center mb-12" data-id="element-1520">
          <h2 className="font-heading text-3xl font-bold text-plum-700" data-id="element-1521">
            More Courses Dropping Soon
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1522">
          {/* The Ultimate Checklist Guide — with embedded MailerLite form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-gradient-to-br from-lavender-50/40 to-white/40 backdrop-blur-sm border border-lavender-200/50 rounded-[2rem] p-8 shadow-sm" data-id="element-1523">
            <div className="absolute top-6 right-6 px-3 py-1 bg-white/80 text-plum-500 text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1 border border-plum-100" data-id="element-1524">
              <LockIcon className="w-3 h-3" data-id="element-1525" /> Coming Soon
            </div>

            <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3 mt-4" data-id="element-1526">
              The Ultimate Checklist Guide
            </h3>
            <p className="text-plum-500 mb-8 font-medium" data-id="element-1527">
              Every checklist you could ever need—all in one place. From daily
              routines to life organization, this guide is designed to help you
              stay on track, reduce overwhelm, and get your life back in order
              one step at a time.
            </p>

            {/* MailerLite Signup Form — Ultimate Checklist Guide */}
            <CourseSignupForm formId="182583406836582077" data-id="element-1528" />
          </motion.div>

          {/* Trippin & Fixin — Coming Soon */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="relative bg-gradient-to-br from-sky-50/40 to-white/40 backdrop-blur-sm border border-sky-200/50 rounded-[2rem] p-8 shadow-sm" data-id="element-1529">
            <div className="absolute top-6 right-6 px-3 py-1 bg-white/80 text-plum-500 text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1 border border-plum-100" data-id="element-1530">
              <LockIcon className="w-3 h-3" data-id="element-1531" /> Coming Soon
            </div>

            <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3 mt-4" data-id="element-1532">
              Trippin & Fixin – From Chaos to Coordination
            </h3>
            <p className="text-plum-500 mb-8 font-medium" data-id="element-1533">
              In this next phase, we take everything you started in The Broken
              Beginning and bring it into real life. This is where we tackle the
              day-to-day chaos—getting organized physically, emotionally, and
              mentally. You'll learn how to rebuild structure, create routines
              that actually work, and start feeling in control of your life
              again. This isn't about perfection—it's about progress,
              consistency, and finally getting your life back together in a way
              that feels good.
            </p>

            {/* MailerLite Signup Form — Trippin & Fixin */}
            <CourseSignupForm formId="182586322711479418" data-id="element-1534" />
          </motion.div>
        </div>
      </section>
    </div>;
}